import React from 'react';
import {
  Paper,
  Typography,
  List,
  ListItem,
  ListItemAvatar,
  Avatar,
  Chip,
  Box,
  Tooltip,
  IconButton,
  Stack
} from '@mui/material';
import {
  Info as InfoIcon,
  Warning as WarningIcon
} from '@mui/icons-material';
import { format, differenceInDays } from 'date-fns';
import { Student } from '@/types';

interface RecentAbsencesProps {
  students: Student[];
}

export const RecentAbsences: React.FC<RecentAbsencesProps> = ({ students }) => {
  const today = new Date();
  const recentAbsences = students
    .filter(s => s.attendanceRate < 90)
    .map(student => ({
      ...student,
      absenceType: student.attendanceRate < 85 ? 'Unexcused' : 'Excused',
      reason: student.attendanceRate < 85 ? 'No Contact' : 'Medical',
      duration: Math.floor((100 - student.attendanceRate) / 10),
      notified: student.attendanceRate > 85
    }))
    .sort((a, b) => b.daysAbsent - a.daysAbsent)
    .slice(0, 8);

  const getAbsenceColor = (type: string) => {
    return type === 'Excused' ? 'info' : 'error';
  };

  const getDaysAgo = (date: string) => {
    if (!date) return 'Not available';
    const days = differenceInDays(today, new Date(date));
    if (days === 0) return 'Today';
    if (days === 1) return 'Yesterday';
    return `${days} days ago`;
  };

  return (
    <Paper sx={{ 
      p: 3, 
      width: '100%',
      height: '100%', 
      display: 'flex', 
      flexDirection: 'column',
      minHeight: 480 
    }}>
      <Typography variant="h6" component="div" gutterBottom>
        Recent Absences
      </Typography>

      <List sx={{ 
        flexGrow: 1, 
        overflow: 'auto',
        '& > li:last-child': {
          borderBottom: 'none'
        }
      }}>
        {recentAbsences.map((student) => (
          <ListItem 
            key={student.id} 
            divider
            sx={{ py: 2 }}
          >
            <ListItemAvatar>
              <Avatar 
                src={student.photo}
                sx={{ 
                  width: 40, 
                  height: 40,
                  border: '2px solid',
                  borderColor: 'background.paper'
                }}
              />
            </ListItemAvatar>
            <Box sx={{ flex: 1 }}>
              <Stack spacing={1}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Typography variant="subtitle2" component="span">
                    {`${student.firstName} ${student.lastName}`}
                  </Typography>
                  <Chip
                    label={`Grade ${student.grade}`}
                    size="small"
                    variant="outlined"
                  />
                  {student.attendanceRate < 85 && (
                    <Tooltip title="Critical attendance rate">
                      <WarningIcon color="error" fontSize="small" />
                    </Tooltip>
                  )}
                </Stack>

                <Stack spacing={1}>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Chip
                      label={student.absenceType}
                      size="small"
                      color={getAbsenceColor(student.absenceType)}
                    />
                    <Typography variant="caption" component="span" color="text.secondary">
                      {getDaysAgo(student.lastAttendanceDate || '')}
                    </Typography>
                    {student.duration > 1 && (
                      <Chip
                        label={`${student.duration} days`}
                        size="small"
                        variant="outlined"
                      />
                    )}
                  </Stack>

                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Typography variant="body2" component="span" color="text.secondary">
                      {student.reason}
                    </Typography>
                    {!student.notified && (
                      <Tooltip title="Parent notification pending">
                        <IconButton size="small">
                          <InfoIcon fontSize="small" color="warning" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Stack>
                </Stack>
              </Stack>
            </Box>
          </ListItem>
        ))}
        {recentAbsences.length === 0 && (
          <ListItem>
            <Box>
              <Typography variant="body1" component="div">
                No recent absences
              </Typography>
              <Typography variant="body2" component="div" color="text.secondary">
                All students are maintaining good attendance
              </Typography>
            </Box>
          </ListItem>
        )}
      </List>
    </Paper>
  );
};